import { useEffect, useState } from "react";
import { Routes, Route, NavLink, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import FileManager from "@/components/FileManager";
import { toast } from "sonner";
import {
  Plus, HeartPulse, LogOut, Building2, Users, Calendar, Wallet, Activity,
  ShieldCheck, MapPin, Zap, CheckCircle2, Clock, Loader2, TrendingUp, AlertTriangle
} from "lucide-react";

const StatCard = ({ label, value, icon: Icon, color = "sky", testid }) => (
  <Card className="p-5 rounded-2xl med-shadow" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs text-slate-500 font-medium">{label}</div>
        <div className="font-mono font-bold text-2xl mt-1 text-slate-900">{value}</div>
      </div>
      <div className={`w-9 h-9 rounded-lg bg-${color}-50 text-${color}-600 flex items-center justify-center`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
  </Card>
);

function Sidebar() {
  const { user, logout } = useAuth();
  const links = [
    { to: "/hospital", label: "Overview", icon: Activity, end: true, tid: "nav-overview" },
    { to: "/hospital/shifts", label: "Shifts", icon: Calendar, tid: "nav-shifts" },
    { to: "/hospital/emergency", label: "Doctor Now", icon: Zap, tid: "nav-emergency" },
    { to: "/hospital/invoices", label: "Invoices", icon: Wallet, tid: "nav-invoices" },
    { to: "/hospital/profile", label: "Profile", icon: Building2, tid: "nav-profile" },
  ];
  return (
    <aside className="hidden lg:flex flex-col fixed left-0 top-0 bottom-0 w-64 bg-[#0B1528] text-white p-5 z-30">
      <div className="flex items-center gap-2 pb-6 border-b border-white/10">
        <div className="w-8 h-8 rounded-lg bg-sky-600 flex items-center justify-center"><HeartPulse className="w-4 h-4" /></div>
        <div><div className="font-extrabold">MedShift</div><div className="text-[10px] uppercase tracking-widest text-sky-300">Hospital OS</div></div>
      </div>
      <nav className="mt-5 space-y-1 flex-1">
        {links.map(l => (
          <NavLink key={l.to} to={l.to} end={l.end} data-testid={l.tid}
            className={({isActive}) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition ${isActive ? "bg-sky-600 text-white" : "text-sky-100/70 hover:bg-white/5"}`}>
            <l.icon className="w-4 h-4" /> {l.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 pt-4">
        <div className="text-sm font-medium">{user?.name}</div>
        <div className="text-[10px] text-sky-300/70">{user?.email}</div>
        <Button size="sm" variant="outline" className="mt-3 w-full bg-transparent border-white/20 text-white hover:bg-white/10" onClick={logout} data-testid="hospital-logout-btn">
          <LogOut className="w-3.5 h-3.5 mr-2" /> Sign out
        </Button>
      </div>
    </aside>
  );
}

function PostShiftDialog({ onCreated, urgency: defaultUrg = "NORMAL", trigger }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    specialty: "DMO", department: "Ward", date: new Date().toISOString().slice(0,10),
    start_time: "20:00", end_time: "08:00", hourly_rate: 900, min_experience: 2,
    required_qualifications: "MBBS", location: "Hospital premises", num_doctors: 1,
    special_instructions: "Report 15 minutes before shift start.", urgency: defaultUrg,
  });
  const submit = async () => {
    try {
      const { data } = await api.post("/hospital/duties", form);
      toast.success(`Duty posted. ${data.matched_doctors} doctors notified.`);
      setOpen(false);
      onCreated?.();
    } catch { toast.error("Could not post duty"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-white sm:max-w-lg">
        <DialogHeader><DialogTitle>Post a requirement</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Specialty</Label>
            <Select value={form.specialty} onValueChange={v => setForm({...form, specialty: v})}>
              <SelectTrigger data-testid="post-specialty"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["DMO", "Intensivist", "Emergency Medicine", "Anaesthesiologist"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Department</Label>
            <Select value={form.department} onValueChange={v => setForm({...form, department: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["ICU","Emergency","Ward","OT","Other"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} data-testid="post-date" /></div>
          <div><Label>Rate (₹/hr)</Label><Input type="number" value={form.hourly_rate} onChange={e => setForm({...form, hourly_rate: parseFloat(e.target.value)})} data-testid="post-rate" /></div>
          <div><Label>Start</Label><Input type="time" value={form.start_time} onChange={e => setForm({...form, start_time: e.target.value})} /></div>
          <div><Label>End</Label><Input type="time" value={form.end_time} onChange={e => setForm({...form, end_time: e.target.value})} /></div>
          <div><Label>Min experience (yrs)</Label><Input type="number" value={form.min_experience} onChange={e => setForm({...form, min_experience: parseFloat(e.target.value)})} /></div>
          <div><Label># Doctors</Label><Input type="number" value={form.num_doctors} onChange={e => setForm({...form, num_doctors: parseInt(e.target.value)})} /></div>
          <div className="col-span-2">
            <Label>Urgency</Label>
            <Select value={form.urgency} onValueChange={v => setForm({...form, urgency: v})}>
              <SelectTrigger data-testid="post-urgency"><SelectValue /></SelectTrigger>
              <SelectContent>{["NORMAL","URGENT","EMERGENCY"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Special instructions</Label><Textarea value={form.special_instructions} onChange={e => setForm({...form, special_instructions: e.target.value})} /></div>
        </div>
        <Button onClick={submit} className="bg-sky-600 hover:bg-sky-700" data-testid="post-submit-btn">Find matching doctors</Button>
      </DialogContent>
    </Dialog>
  );
}

function MatchesDialog({ dutyId, open, setOpen, onBooked }) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!open || !dutyId) return;
    setLoading(true);
    api.get(`/hospital/matches/${dutyId}`).then(({data}) => setMatches(data)).finally(() => setLoading(false));
  }, [open, dutyId]);
  const book = async (doctor_id) => {
    try { await api.post(`/hospital/duties/${dutyId}/book`, { doctor_id }); toast.success("Booked. Doctor notified."); setOpen(false); onBooked?.(); }
    catch { toast.error("Booking failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-white sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Best matched doctors</DialogTitle></DialogHeader>
        {loading ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-sky-600" /></div>
          : matches.length === 0 ? <div className="text-sm text-slate-500 py-6 text-center">No verified doctors matching this shift yet.</div>
          : matches.map(d => (
            <Card key={d.user_id} className="p-4 rounded-2xl flex items-center gap-4" data-testid={`match-${d.user_id}`}>
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-sky-500 to-sky-700 text-white flex items-center justify-center font-bold">{d.full_name?.[0]}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{d.full_name}</span>
                  <ShieldCheck className="w-4 h-4 text-emerald-500" />
                </div>
                <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                  <span>{(d.specialties || []).join(", ")}</span>
                  <span>· {d.experience_years}y exp</span>
                  <span>· <MapPin className="w-3 h-3 inline" /> {d.distance_km} km</span>
                  <span>· ⭐ {d.reliability_score} · {d.completed_duties} duties</span>
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono font-bold text-emerald-600">{d.match_score}%</div>
                <div className="text-[10px] text-slate-500">₹{d.hourly_rate}/hr</div>
                <Button size="sm" className="mt-2 bg-sky-600 hover:bg-sky-700 rounded-full" onClick={() => book(d.user_id)} data-testid={`book-doctor-btn-${d.user_id}`}>Book</Button>
              </div>
            </Card>
          ))}
      </DialogContent>
    </Dialog>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  const [duties, setDuties] = useState([]);
  const [matchDuty, setMatchDuty] = useState(null);
  const load = async () => {
    const [{data: s}, {data: d}] = await Promise.all([
      api.get("/hospital/dashboard"), api.get("/hospital/duties")
    ]);
    setStats(s); setDuties(d);
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try { await api.post(`/hospital/duties/${id}/approve`); toast.success("Duty approved. Payment released."); load(); }
    catch { toast.error("Approve failed"); }
  };

  if (!stats) return <div className="p-10"><Loader2 className="w-6 h-6 animate-spin text-sky-600" /></div>;
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="hospital-overview">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Command center</h1>
          <p className="text-sm text-slate-500 mt-1">Live view of your active shifts, matches and payouts.</p>
        </div>
        <PostShiftDialog onCreated={load} trigger={
          <Button className="bg-sky-600 hover:bg-sky-700 rounded-full px-5 h-11" data-testid="post-shift-btn"><Plus className="w-4 h-4 mr-1" /> Post requirement</Button>
        } />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard testid="stat-open" label="Open shifts" value={stats.open_shifts} icon={Clock} color="sky" />
        <StatCard testid="stat-filled" label="Filled" value={stats.filled_shifts} icon={CheckCircle2} color="emerald" />
        <StatCard testid="stat-emergency" label="Emergency" value={stats.emergency} icon={AlertTriangle} color="red" />
        <StatCard testid="stat-today" label="Today" value={stats.today} icon={Calendar} color="sky" />
        <StatCard testid="stat-month" label="Monthly cost" value={`₹${(stats.monthly_cost || 0).toLocaleString()}`} icon={TrendingUp} color="emerald" />
      </div>

      <Card className="rounded-2xl med-shadow">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-bold">Your shifts</h3>
          <Badge className="bg-sky-50 text-sky-700 border border-sky-200">Avg match {stats.avg_match_minutes} min</Badge>
        </div>
        <div className="divide-y divide-slate-100">
          {duties.length === 0 && <div className="p-8 text-center text-sm text-slate-500">Post your first requirement to get started.</div>}
          {duties.map(d => (
            <div key={d.duty_id} className="p-5 flex items-center gap-4 flex-wrap" data-testid={`hospital-duty-${d.duty_id}`}>
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2">
                  <Badge className="bg-slate-100 text-slate-700 rounded-full text-[10px]">{d.specialty}</Badge>
                  <Badge className={`rounded-full text-[10px] ${d.urgency === "EMERGENCY" ? "bg-red-50 text-red-700 border border-red-200" : "bg-slate-50 border border-slate-200 text-slate-700"}`}>{d.urgency}</Badge>
                  <Badge className="bg-white border border-slate-200 text-slate-700 rounded-full text-[10px]">{d.status}</Badge>
                </div>
                <div className="mt-2 text-sm"><span className="font-semibold">{d.department}</span> · {d.date} · {d.start_time}–{d.end_time} · <span className="font-mono">₹{d.hourly_rate}/hr</span></div>
                {d.doctor && (
                  <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3 text-emerald-500" /> {d.doctor.name}
                    {d.attendance?.check_in && <span> · Checked in {d.attendance.check_in.slice(11,16)}</span>}
                    {d.attendance?.check_out && <span> · Out {d.attendance.check_out.slice(11,16)} · ₹{d.attendance.amount}</span>}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                {d.status === "OPEN" && <Button size="sm" className="rounded-full bg-sky-600 hover:bg-sky-700" onClick={() => setMatchDuty(d.duty_id)} data-testid={`view-matches-btn-${d.duty_id}`}>View matches</Button>}
                {d.status === "COMPLETED" && !d.attendance?.approved && <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => approve(d.duty_id)} data-testid={`approve-duty-btn-${d.duty_id}`}>Approve & pay</Button>}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <MatchesDialog dutyId={matchDuty} open={!!matchDuty} setOpen={(v) => !v && setMatchDuty(null)} onBooked={load} />
    </div>
  );
}

function EmergencyTab() {
  const [posting, setPosting] = useState(false);
  const trigger = async () => {
    setPosting(true);
    try {
      const { data } = await api.post("/hospital/duties", {
        specialty: "Intensivist", department: "ICU",
        date: new Date().toISOString().slice(0,10),
        start_time: new Date().toISOString().slice(11,16), end_time: "23:59",
        hourly_rate: 1800, min_experience: 3,
        required_qualifications: "MD/DNB Critical Care", required_skills: ["Ventilator","ACLS"],
        location: "ICU", num_doctors: 1, urgency: "EMERGENCY",
        special_instructions: "🚨 Immediate need. Broadcast in effect.",
      });
      toast.success(`🚨 Broadcast sent to ${data.matched_doctors} verified doctors.`);
    } catch { toast.error("Emergency dispatch failed"); }
    setPosting(false);
  };
  return (
    <div className="p-6 lg:p-8" data-testid="emergency-tab">
      <h1 className="text-3xl font-extrabold tracking-tight">Emergency dispatch</h1>
      <p className="text-sm text-slate-500 mt-1">One tap broadcasts to every eligible verified doctor in radius. Avg response: 12 minutes.</p>
      <Card className="mt-6 p-8 rounded-3xl bg-[#0B1528] text-white relative overflow-hidden">
        <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-red-500/20 blur-3xl"></div>
        <div className="relative">
          <Badge className="bg-red-500/20 text-red-200 border border-red-400/30">🚨 Doctor Now</Badge>
          <h3 className="mt-4 text-2xl font-bold">Need an ICU Intensivist right now?</h3>
          <p className="text-sky-200/80 mt-2 max-w-md">First accept wins. Automatic replacement if the doctor cancels.</p>
          <Button size="lg" onClick={trigger} disabled={posting} className="mt-6 bg-red-500 hover:bg-red-600 rounded-full px-6 h-12" data-testid="emergency-broadcast-btn">
            {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Zap className="w-4 h-4 mr-2" /> Trigger emergency broadcast</>}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function InvoicesTab() {
  const [duties, setDuties] = useState([]);
  const [paying, setPaying] = useState(null);
  const load = () => api.get("/hospital/duties").then(({data}) => setDuties(data.filter(d => d.attendance))).catch(() => {});
  useEffect(() => { load(); }, []);

  const loadRazorpay = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const pay = async (d) => {
    setPaying(d.duty_id);
    try {
      const { data: order } = await api.post("/payments/create-order", { duty_id: d.duty_id });
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load payment gateway"); setPaying(null); return; }
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "MedShift",
        description: `${order.invoice_no} · ${order.specialty} duty`,
        order_id: order.order_id,
        theme: { color: "#0284C7" },
        handler: async (res) => {
          try {
            await api.post("/payments/verify", {
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast.success("Payment confirmed. Escrow released to doctor.");
            load();
          } catch { toast.error("Payment verification failed"); }
        },
        modal: { ondismiss: () => setPaying(null) },
      });
      rzp.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start payment");
    }
    setPaying(null);
  };

  return (
    <div className="p-6 lg:p-8 space-y-4" data-testid="invoices-tab">
      <h1 className="text-3xl font-extrabold tracking-tight">Invoices & escrow</h1>
      <Card className="p-0 rounded-2xl overflow-hidden">
        <div className="grid grid-cols-7 gap-2 p-4 text-xs uppercase tracking-widest text-slate-500 font-mono border-b border-slate-100">
          <div>Invoice</div><div>Doctor</div><div>Shift</div><div>Hours</div><div>Amount</div><div>Status</div><div></div>
        </div>
        {duties.length === 0 && <div className="p-8 text-center text-sm text-slate-500">No invoices yet.</div>}
        {duties.map(d => (
          <div key={d.duty_id} className="grid grid-cols-7 gap-2 p-4 text-sm border-b border-slate-100 items-center">
            <div className="font-mono">INV-{d.duty_id.slice(-6).toUpperCase()}</div>
            <div>{d.doctor?.name || "—"}</div>
            <div>{d.specialty} · {d.date}</div>
            <div className="font-mono">{d.attendance?.hours_worked || 0}h</div>
            <div className="font-mono font-semibold">₹{d.attendance?.amount || 0}</div>
            <div>{d.attendance?.approved ? <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">Paid</Badge> : <Badge className="bg-amber-50 text-amber-700 border border-amber-200">Pending</Badge>}</div>
            <div>
              {!d.attendance?.approved && (
                <Button size="sm" className="rounded-full bg-sky-600 hover:bg-sky-700" disabled={paying === d.duty_id} onClick={() => pay(d)} data-testid={`pay-invoice-btn-${d.duty_id}`}>
                  {paying === d.duty_id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Pay"}
                </Button>
              )}
            </div>
          </div>
        ))}
      </Card>
      <p className="text-xs text-slate-500">Total = doctor payout + 10% platform fee. Powered by Razorpay (UPI, cards, netbanking).</p>
    </div>
  );
}

function ShiftsTab() { return <Overview />; }

function ProfileTab() {
  const [h, setH] = useState(null);
  useEffect(() => { api.get("/hospital/me").then(({data}) => setH(data)).catch(() => {}); }, []);
  if (!h) return <div className="p-10"><Loader2 className="w-5 h-5 animate-spin text-sky-600" /></div>;
  const save = async () => { try { await api.put("/hospital/me", h); toast.success("Saved"); } catch { toast.error("Save failed"); } };
  return (
    <div className="p-6 lg:p-8 space-y-4" data-testid="hospital-profile-tab">
      <h1 className="text-3xl font-extrabold tracking-tight">Hospital profile</h1>
      <Card className="p-6 rounded-2xl grid grid-cols-2 gap-4">
        <div><Label>Hospital name</Label><Input value={h.hospital_name || ""} onChange={e => setH({...h, hospital_name: e.target.value})} data-testid="hp-name" /></div>
        <div><Label>Type</Label><Input value={h.hospital_type || ""} onChange={e => setH({...h, hospital_type: e.target.value})} /></div>
        <div><Label>Area</Label><Input value={h.area || ""} onChange={e => setH({...h, area: e.target.value})} data-testid="hp-area" /></div>
        <div><Label>Address</Label><Input value={h.address || ""} onChange={e => setH({...h, address: e.target.value})} /></div>
        <div><Label>Phone</Label><Input value={h.phone || ""} onChange={e => setH({...h, phone: e.target.value})} /></div>
        <div><Label>Bed capacity</Label><Input type="number" value={h.bed_capacity || 0} onChange={e => setH({...h, bed_capacity: parseInt(e.target.value)})} /></div>
        <div><Label>ICU beds</Label><Input type="number" value={h.icu_beds || 0} onChange={e => setH({...h, icu_beds: parseInt(e.target.value)})} /></div>
        <div><Label>License no.</Label><Input value={h.license_no || ""} onChange={e => setH({...h, license_no: e.target.value})} /></div>
        <div className="col-span-2"><Button onClick={save} className="bg-sky-600 hover:bg-sky-700 rounded-xl" data-testid="save-hospital-btn">Save</Button></div>
      </Card>
      <FileManager docType="hospital_document" title="Hospital verification documents" testid="hospital-file-manager" />
    </div>
  );
}

function MobileTopBar() {
  const { logout } = useAuth();
  return (
    <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-slate-200 h-14 flex items-center justify-between px-4">
      <div className="flex items-center gap-2"><div className="w-7 h-7 rounded-lg bg-[#0B1528] flex items-center justify-center"><HeartPulse className="w-3.5 h-3.5 text-sky-400" /></div><span className="font-extrabold text-sm">MedShift</span></div>
      <Button size="sm" variant="ghost" onClick={logout} data-testid="mobile-logout"><LogOut className="w-4 h-4" /></Button>
    </header>
  );
}

export default function HospitalDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (loading) return;
    if (!user) navigate("/");
    else if (user.role && user.role !== "hospital" && user.role !== "admin") navigate("/choose-role");
  }, [user, loading, navigate]);
  if (loading || !user) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-sky-600" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <MobileTopBar />
      <main className="lg:ml-64">
        <Routes>
          <Route index element={<Overview />} />
          <Route path="shifts" element={<ShiftsTab />} />
          <Route path="emergency" element={<EmergencyTab />} />
          <Route path="invoices" element={<InvoicesTab />} />
          <Route path="profile" element={<ProfileTab />} />
        </Routes>
      </main>
    </div>
  );
}
